import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import PublicNavBar from "../components/PublicNavBar";
import PublicFooter from "../components/PublicFooter";
import { ChevronRight, FileText, ScrollText, Cookie, Shield, Receipt, Ban, AlertTriangle, Accessibility, Building2, BarChart3 } from "lucide-react";
import {
  POLICIES, POLICY_ORDER, EFFECTIVE_DATE, COMPANY, getPolicy,
} from "./legalContent";
import { api } from "../api";
import usePageTitle from "../hooks/usePageTitle";

const ICONS = {
  "privacy": Shield,
  "terms": ScrollText,
  "cookies": Cookie,
  "grievance": FileText,
  "refunds": Receipt,
  "acceptable-use": Ban,
  "disclaimer": AlertTriangle,
  "accessibility": Accessibility,
  "transparency": BarChart3,
  "about": Building2,
};

/**
 * Renders any policy by URL slug — /legal/:slug.
 *
 * Layout:
 *   - PublicNavBar
 *   - Hero strip with title + eyebrow + last-updated
 *   - Two-column grid: left = sticky TOC (anchors to each section);
 *                       right = body (paragraphs / bullets / tables)
 *   - Compact PublicFooter
 */
export default function LegalPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const staticPolicy = useMemo(() => getPolicy(slug), [slug]);
  const [override, setOverride] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Try the API first; fall back to the bundled static defaults.
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    setLoaded(false);
    api.get(`/legal/policies/${slug}`)
      .then((r) => { if (alive) setOverride(r.data); })
      .catch(() => { if (alive) setOverride(null); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [slug]);

  const policy = useMemo(() => {
    if (!override) return staticPolicy;
    // Merge — preserve slug + eyebrow defaults if admin left blank
    return {
      ...staticPolicy,
      ...override,
      slug,
      eyebrow: override.eyebrow || staticPolicy?.eyebrow,
    };
  }, [override, staticPolicy, slug]);

  usePageTitle(policy ? `${policy.title} · ${COMPANY.brand}` : "Legal");

  // Bounce unknown slugs (no static default AND no DB override) back to the index
  useEffect(() => {
    if (loaded && !policy) navigate("/legal", { replace: true });
  }, [loaded, policy, navigate]);
  if (!policy) return null;

  const effectiveDate = override?.updated_at
    ? new Date(override.updated_at).toLocaleDateString(undefined,
        { day: "numeric", month: "long", year: "numeric" })
    : EFFECTIVE_DATE;

  return (
    <div className="min-h-screen bg-paper" data-testid={`legal-${policy.slug}-page`}>
      <PublicNavBar />

      {/* Hero */}
      <section className="border-b border-rule bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-10 md:py-14">
          <nav className="text-xs text-muted inline-flex items-center gap-1">
            <Link to="/" className="hover:text-signal">Home</Link>
            <ChevronRight size={10}/>
            <Link to="/legal" className="hover:text-signal">Legal</Link>
            <ChevronRight size={10}/>
            <span className="text-default">{policy.title}</span>
          </nav>
          <div className="mt-3 flex items-start gap-3">
            <span className="eyebrow">{policy.eyebrow}</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-1">
            {policy.title}
          </h1>
          <p className="text-muted text-sm mt-3">
            Effective date: <b>{effectiveDate}</b> · {COMPANY.legalName} · Pune, India ·
            Governed by Indian law.
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-10 grid md:grid-cols-12 gap-10">
        {/* Sticky TOC */}
        <aside className="md:col-span-3" data-testid="legal-toc">
          <div className="sticky top-20 space-y-1">
            <div className="eyebrow mb-2">On this page</div>
            <nav className="space-y-1 text-sm">
              {policy.sections.map((s, i) => (
                <a
                  key={i}
                  href={`#sec-${i}`}
                  className="block text-muted hover:text-signal transition truncate"
                  data-testid={`legal-toc-link-${i}`}
                >
                  {s.heading}
                </a>
              ))}
            </nav>

            <div className="mt-6 pt-6 border-t border-rule">
              <div className="eyebrow mb-2">Related</div>
              <ul className="space-y-1 text-sm">
                {POLICY_ORDER.filter((s) => s !== policy.slug).slice(0, 5).map((s) => {
                  const p = getPolicy(s);
                  return (
                    <li key={s}>
                      <Link to={`/legal/${s}`} className="text-muted hover:text-signal">
                        {p?.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </aside>

        {/* Article */}
        <article className="md:col-span-9 prose-base max-w-none">
          <p className="text-default leading-relaxed text-base" data-testid="legal-intro">
            {policy.intro}
          </p>

          {policy.sections.map((s, i) => (
            <section key={i} id={`sec-${i}`} className="mt-10 scroll-mt-24"
                     data-testid={`legal-section-${i}`}>
              <h2 className="font-display font-bold text-2xl">{s.heading}</h2>

              {(s.paras || []).map((para, j) => (
                <p key={j} className="text-default leading-relaxed mt-3">
                  {para}
                </p>
              ))}

              {Array.isArray(s.bullets) && s.bullets.length > 0 && (
                <ul className="list-disc pl-6 mt-3 space-y-1.5 text-default leading-relaxed">
                  {s.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              )}

              {s.table && (
                <div className="overflow-x-auto mt-4 border border-rule rounded-sm">
                  <table className="dense w-full text-sm">
                    <thead>
                      <tr>
                        {s.table.head.map((h, j) => <th key={j} className="text-left">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {s.table.rows.map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => <td key={ci} className="align-top">{cell}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

          {/* Footer of the policy: contact + acknowledgement */}
          <div className="mt-12 pt-6 border-t border-rule text-sm text-muted">
            Questions? Write to <a href={`mailto:${COMPANY.contactEmail}`} className="text-signal hover:underline">{COMPANY.contactEmail}</a>.
            Privacy concerns to <a href={`mailto:${COMPANY.dpoEmail}`} className="text-signal hover:underline">{COMPANY.dpoEmail}</a>.
            Formal grievances to <a href={`mailto:${COMPANY.grievanceEmail}`} className="text-signal hover:underline">{COMPANY.grievanceEmail}</a>.
          </div>
        </article>
      </section>

      <PublicFooter compact />
    </div>
  );
}

/** Index page at /legal — directory of all policies. */
export function LegalIndex() {
  usePageTitle(`Legal · ${COMPANY.brand}`);
  return (
    <div className="min-h-screen bg-paper" data-testid="legal-index-page">
      <PublicNavBar />
      <section className="border-b border-rule bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-12 md:py-16">
          <span className="eyebrow">Mandatory disclosures</span>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-1">Legal &amp; policies</h1>
          <p className="text-muted mt-3 max-w-2xl text-sm">
            How we handle data, what you can and can&apos;t do, how to raise a concern, refund rules, and our corporate disclosures.
            Published in compliance with the DPDP Act 2023, IT Rules 2021, Consumer Protection (E-Commerce) Rules 2020, and the Companies Act 2013.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-8 py-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {POLICY_ORDER.map((s) => {
          const p = getPolicy(s);
          const Icon = ICONS[s] || FileText;
          return (
            <Link
              key={s}
              to={`/legal/${s}`}
              className="card card-pad hover:border-signal transition group"
              data-testid={`legal-card-${s}`}
            >
              <div className="flex items-start gap-3">
                <div className="rounded-sm bg-signal/10 text-signal p-2 mt-0.5">
                  <Icon size={16}/>
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg">{p.title}</div>
                  <div className="text-xs uppercase tracking-wider text-muted mt-0.5">{p.eyebrow}</div>
                  <p className="text-sm text-muted mt-2 line-clamp-3">{p.intro}</p>
                </div>
              </div>
              <div className="text-xs text-signal mt-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                Read <ChevronRight size={10}/>
              </div>
            </Link>
          );
        })}
      </section>

      <PublicFooter compact />
    </div>
  );
}
